'use es6';

import { fromJS, List, OrderedSet } from 'immutable';
import { SEARCH, AGGREGATION } from '../../../constants/configTypes';
import { COUNT, DISTINCT_APPROX, AVG } from '../../../constants/metricTypes';
import { DESC } from '../../../constants/sortOrder';
import { Config } from '../..';
import { Metric } from '../../metrics';
import { makeFilters } from '../filters';
import { ATTRIBUTION_TOUCH_POINTS, DEALS } from '../../../constants/dataTypes';
import { defaultProperties } from '../defaultProperties';
import { buildDrilldownReport } from '../report';
import { has } from '../../../lib/has';
import { propertyLabelTranslator } from 'property-translator/propertyTranslator';
import campaign from '../../../references/campaign';
import company from '../../../references/company';
import contact from '../../../references/contact';
import deal from '../../../references/deal';
import owner from '../../../references/owner';
import pipeline from '../../../references/pipeline';
import { getDimensions } from '../../../report/configReportGetters';
export const getSetOfDefaultProperties = (propertiesToHide = List()) => {
  const propertiesToDisplay = defaultProperties[ATTRIBUTION_TOUCH_POINTS].filter(property => !propertiesToHide.includes(property));
  return OrderedSet.of(...propertiesToDisplay);
};
const idLookup = referenceLookup => id => referenceLookup(List([id])).then(v => v.valueSeq().getIn([0, 'label']));
const isIdPropertyInCampaignDrilldown = (baseReport, keys) => ['hs_deal_id', 'hs_contact_id'].includes(keys.get(-2)) && getDimensions(baseReport).first() === 'hs_deal_id';
const isDistinctCountProperty = (baseReport, keys) => keys.last() === DISTINCT_APPROX || isIdPropertyInCampaignDrilldown(baseReport, keys);
const propertyReferences = {
  hs_campaign_name: idLookup(campaign),
  hs_contact_company: idLookup(company),
  hs_contact_id: idLookup(contact),
  hs_deal_id: idLookup(deal),
  hs_deal_pipeline: pipelineId => pipeline(DEALS)().then(options => {
    const matches = options.filter(({
      value
    }) => value === pipelineId);
    if (matches && matches.length) {
      return matches[0].label;
    }
    return null;
  }),
  hs_deal_owner: idLookup(owner)
};
const buildDrilldownName = (baseReport, keys) => {
  const rawLabels = keys.skipLast(2) // The last two keys are the metric and its type
  .zip(baseReport.getIn(['config', 'dimensions'])).map(([value, property]) => ({
    value,
    property
  }));
  if (rawLabels.isEmpty()) {
    return propertyLabelTranslator(keys.first(), 'attributionProperties');
  }
  const hydratedLabelPromises = rawLabels.map(({
    property,
    value
  }) => {
    const propertyLabel = propertyLabelTranslator(property.toLowerCase(), 'attributionProperties');
    let valuePromise;
    if (has(propertyReferences, property)) {
      valuePromise = propertyReferences[property](value);
    } else {
      valuePromise = Promise.resolve(propertyLabelTranslator(value.toLowerCase(), 'attributionProperties'));
    }
    return valuePromise.then(valueLabel => `${propertyLabel}: ${valueLabel}`);
  });
  return Promise.all(hydratedLabelPromises).then(labels => labels.join(' & '));
};
export const getDrilldownConfigType = (baseReport, keys) => {
  if (isDistinctCountProperty(baseReport, keys)) {
    return AGGREGATION;
  } else {
    return SEARCH;
  }
};
const getDrilldownDimensions = (baseReport, keys) => {
  if (isDistinctCountProperty(baseReport, keys)) {
    return List.of(keys.get(-2));
  }
  return List();
};
const getReportRevenueMetrics = baseReport => baseReport.getIn(['config', 'metrics'], List()).filter(m => ['hs_linear_amount', 'hs_linear_amount_pro', 'hs_u_amount', 'hs_w_amount', 'hs_full_path_amount'].includes(m.get('property')));
export const isCampaignDealDrilldown = (baseReport, keys) => keys.size > 2 && baseReport.getIn(['config', 'dimensions']).first() === 'hs_deal_id' && !!baseReport.getIn(['config', 'filters', 'custom']).find(filter => filter.get('property') === 'hs_campaign_name');
export const getDrilldownMetrics = (baseReport, keys) => {
  if (isDistinctCountProperty(baseReport, keys)) {
    const clickedMetric = keys.get(-2);
    if (clickedMetric === 'hs_contact_id') {
      return getReportRevenueMetrics(baseReport);
    } else if (clickedMetric === 'hs_deal_id') {
      return getReportRevenueMetrics(baseReport).unshift(Metric({
        property: 'hs_deal_amount',
        metricTypes: [AVG]
      }));
    }
    return List();
  } else {
    const metricProperties = baseReport.getIn(['config', 'metrics']).map(metric => metric.get('property'));
    const drilldownMetrics = getSetOfDefaultProperties(baseReport.getIn(['displayParams', 'hidePropertiesOnDrilldown'], List())).concat(...metricProperties).map(property => Metric({
      property,
      metricTypes: [COUNT]
    })).toList();
    if (isCampaignDealDrilldown(baseReport, keys)) {
      return drilldownMetrics.insert(2, Metric({
        property: 'hs_campaign_name',
        metricTypes: [COUNT]
      }));
    }
    return drilldownMetrics;
  }
};
const getDrilldownSort = (baseReport, keys) => {
  if (keys.last() === DISTINCT_APPROX) {
    return fromJS([{
      order: DESC,
      property: getDrilldownMetrics(baseReport, keys).getIn([-1, 'property'])
    }]);
  } else {
    return fromJS([{
      property: 'hs_touchpoint_timestamp',
      order: DESC
    }]);
  }
};
export const removeCampaignFilter = filters => filters = filters.set('custom', filters.get('custom').filterNot(filter => filter.get('property') === 'hs_campaign_name'));
const buildDrilldown = (baseReport, keys, options) => {
  return makeFilters(baseReport.get('config'), keys, options).then(filters => {
    if (isCampaignDealDrilldown(baseReport, keys)) {
      filters = removeCampaignFilter(filters);
    }
    return Config({
      configType: getDrilldownConfigType(baseReport, keys),
      dataType: ATTRIBUTION_TOUCH_POINTS,
      dimensions: getDrilldownDimensions(baseReport, keys),
      filters,
      limit: baseReport.getIn(['config', 'limit'], 5),
      metrics: getDrilldownMetrics(baseReport, keys),
      sort: getDrilldownSort(baseReport, keys)
    });
  }).then(generatedConfig => buildDrilldownReport(baseReport, generatedConfig, options));
};
const getAttributionDrilldown = (baseReport, keys, options) => {
  return Promise.all([buildDrilldown(baseReport, keys, options), buildDrilldownName(baseReport, keys)]).then(([drilldownReport, drilldownName]) => {
    return drilldownReport.set('name', drilldownName).setIn(['displayParams', 'columns'], fromJS({
      hs_asset_url: {
        hideColumn: true
      }
    }));
  });
};
export default {
  isSupported: report => report.getIn(['config', 'dataType']) === ATTRIBUTION_TOUCH_POINTS,
  generate: getAttributionDrilldown
};